/**
 * FinanceApp - Copyright (c) 2026 Emmanuel TESTE
 * Tous droits réservés - Logiciel propriétaire
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import CustomerForm from '../components/crm/CustomerForm';
import ImportInvoiceArchive from '../components/crm/ImportInvoiceArchive';

const SORT_OPTIONS = [
  { value: 'name',          label: 'Nom' },
  { value: 'created_at',    label: 'Date création' },
  { value: 'city',          label: 'Ville' },
  { value: 'payment_terms', label: 'Conditions paiement' },
  { value: 'credit_limit',  label: 'Plafond crédit' },
  { value: 'status',        label: 'Statut' },
];

const STATUS_COLORS = {
  prospect:'#185fa5', client_actif:'#1a9e72', client_inactif:'#64748b',
  partenaire:'#8b5cf6', fournisseur:'#ba7517',
};

function fmt(n)    { return n == null ? '—' : Number(n).toLocaleString('fr-FR'); }
function fmtDate(d){ return d ? new Date(d).toLocaleDateString('fr-FR') : '—'; }

// ── Modal wrapper ────────────────────────────────────────────────
function FormModal({ title, children }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ width:920, maxWidth:'96vw', maxHeight:'85vh', display:'flex', flexDirection:'column' }}>
        <div className="modal-header" style={{ flexShrink:0 }}>
          <h3 className="modal-title">{title}</h3>
        </div>
        <div style={{ overflowY:'auto', flex:1, padding:'0 4px' }}>{children}</div>
      </div>
    </div>
  );
}

// ── Sociétés du groupe rattachées au client ─────────────────────
function CompanyAllocationSection({ client }) {
  const [rows, setRows]           = useState([]);
  const [allCompanies, setAll]    = useState([]);
  const [loading, setLoading]     = useState(true);
  const [adding, setAdding]       = useState(false);
  const [newCompanyId, setNewCo]  = useState('');
  const [newAccountCode, setCode] = useState('');
  const [error, setError]         = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.customers.getCompanies(client.id), api.companies.list()])
      .then(([rows, companies]) => { setRows(rows); setAll(Array.isArray(companies) ? companies : (companies?.data || [])); })
      .catch(e => setError(e.message || 'Erreur'))
      .finally(() => setLoading(false));
  }, [client.id]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newCompanyId || !newAccountCode.trim()) { setError('Société et numéro de compte requis'); return; }
    try {
      await api.customers.assignCompany(client.id, { company_id: Number(newCompanyId), account_code: newAccountCode.trim() });
      setNewCo(''); setCode(''); setAdding(false); setError(''); load();
    } catch (e) { setError(e.message || 'Erreur'); }
  };

  const handleRemove = async (companyId) => {
    if (!window.confirm('Retirer ce client de cette société ? Il restera actif sur les autres.')) return;
    try { await api.customers.unassignCompany(client.id, companyId); load(); }
    catch (e) { alert(e.message || 'Erreur'); }
  };

  const availableToAdd = allCompanies.filter(c => !rows.some(r => r.company_id === c.id));

  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <div style={{ fontWeight:700, fontSize:11, color:'var(--text-muted)', textTransform:'uppercase' }}>Sociétés du groupe</div>
        <button className="btn sm" onClick={() => setAdding(a => !a)}>{adding ? 'Annuler' : '+ Ajouter'}</button>
      </div>
      {error && <div className="alert danger" style={{ fontSize:11, marginBottom:8 }}>{error}</div>}
      {loading ? <div style={{ fontSize:12, color:'var(--text-muted)' }}>Chargement…</div> : (
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {rows.map(r => (
            <div key={r.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'#f9fafb', borderRadius:8, padding:'8px 10px' }}>
              <div>
                <div style={{ fontSize:13, fontWeight:600 }}>{r.company_name}</div>
                <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'monospace' }}>Compte {r.account_code}</div>
              </div>
              <button className="btn sm" style={{ color:'#e24b4a' }} onClick={() => handleRemove(r.company_id)}>Retirer</button>
            </div>
          ))}
          {rows.length === 0 && <div style={{ fontSize:12, color:'var(--text-muted)' }}>Aucune société rattachée</div>}
        </div>
      )}
      {adding && (
        <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
          <select className="form-control" style={{ flex:1, minWidth:140 }} value={newCompanyId} onChange={e => setNewCo(e.target.value)}>
            <option value="">Société…</option>
            {availableToAdd.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input className="form-control" style={{ width:140 }} placeholder="N° compte (ex: 411xxx)" value={newAccountCode} onChange={e => setCode(e.target.value)} />
          <button className="btn primary sm" onClick={handleAdd}>Valider</button>
        </div>
      )}
    </div>
  );
}

// ── Commercial (interne ou externe) assigné au client ───────────
function SalesRepSection({ client, currentCompanyId }) {
  const [rows, setRows]         = useState([]);
  const [reps, setReps]         = useState({ internal:[], external:[] });
  const [loading, setLoading]   = useState(true);
  const [adding, setAdding]     = useState(false);
  const [repType, setRepType]   = useState('interne');
  const [repId, setRepId]       = useState('');
  const [customRate, setRate]   = useState('');
  const [error, setError]       = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.customers.getSalesReps(client.id), api.customers.availableReps(currentCompanyId)])
      .then(([allocs, avail]) => { setRows(allocs); setReps(avail || { internal:[], external:[] }); })
      .catch(e => setError(e.message || 'Erreur'))
      .finally(() => setLoading(false));
  }, [client.id, currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!repId) { setError('Commercial requis'); return; }
    try {
      await api.customers.assignSalesRep(client.id, {
        company_id: currentCompanyId, rep_type: repType, rep_id: Number(repId),
        custom_rate: customRate ? Number(customRate) : undefined,
      });
      setRepId(''); setRate(''); setAdding(false); setError(''); load();
    } catch (e) { setError(e.message || 'Erreur'); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Retirer ce commercial de ce client ?')) return;
    try { await api.customers.unassignSalesRep(client.id, id); load(); }
    catch (e) { alert(e.message || 'Erreur'); }
  };

  const options = repType === 'interne' ? reps.internal : reps.external;

  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <div style={{ fontWeight:700, fontSize:11, color:'var(--text-muted)', textTransform:'uppercase' }}>Commercial</div>
        <button className="btn sm" onClick={() => setAdding(a => !a)}>{adding ? 'Annuler' : '+ Assigner'}</button>
      </div>
      {error && <div className="alert danger" style={{ fontSize:11, marginBottom:8 }}>{error}</div>}
      {loading ? <div style={{ fontSize:12, color:'var(--text-muted)' }}>Chargement…</div> : (
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {rows.map(r => (
            <div key={r.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'#f9fafb', borderRadius:8, padding:'8px 10px' }}>
              <div>
                <div style={{ fontSize:13, fontWeight:600 }}>
                  {r.rep_name}
                  <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:6, marginLeft:6,
                    background: r.rep_type === 'interne' ? '#1a9e7218' : '#ba751718',
                    color: r.rep_type === 'interne' ? '#1a9e72' : '#ba7517' }}>{r.rep_type}</span>
                </div>
                <div style={{ fontSize:11, color:'var(--text-muted)' }}>{r.company_name} · {r.custom_rate ?? r.default_rate ?? 0}%</div>
              </div>
              <button className="btn sm" style={{ color:'#e24b4a' }} onClick={() => handleRemove(r.id)}>Retirer</button>
            </div>
          ))}
          {rows.length === 0 && <div style={{ fontSize:12, color:'var(--text-muted)' }}>Aucun commercial assigné</div>}
        </div>
      )}
      {adding && (
        <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
          <select className="form-control" style={{ width:90 }} value={repType} onChange={e => { setRepType(e.target.value); setRepId(''); }}>
            <option value="interne">Interne</option>
            <option value="externe">Externe</option>
          </select>
          <select className="form-control" style={{ flex:1, minWidth:140 }} value={repId} onChange={e => setRepId(e.target.value)}>
            <option value="">Commercial…</option>
            {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <input className="form-control" style={{ width:80 }} placeholder="Taux %" value={customRate} onChange={e => setRate(e.target.value)} />
          <button className="btn primary sm" onClick={handleAdd}>Valider</button>
        </div>
      )}
    </div>
  );
}

// ── Fiche détail (lecture) ─────────────────────────────────────
function DetailModal({ client, currentCompanyId, onEdit, onDelete, onClose }) {
  const [deactivating, setDeactivating] = useState(false);

  const handleDeactivate = async () => {
    const reason = window.prompt(
      `Motif de désactivation de "${client.name}" (fermeture d'activité, etc.) — laisser vide si non précisé :`
    );
    if (reason === null) return; // annulé par l'utilisateur
    if (!window.confirm(`Confirmer la désactivation de "${client.name}" ? Il ne sera plus visible sauf réactivation.`)) return;
    setDeactivating(true);
    try { await api.customers.setStatus(client.id, false, reason || null); onDelete(); }
    catch (err) { alert(err.message || 'Erreur lors de la désactivation.'); }
    finally { setDeactivating(false); }
  };

  const handleReactivate = async () => {
    if (!window.confirm(`Réactiver "${client.name}" ? Il redeviendra visible partout dans l'application.`)) return;
    setDeactivating(true);
    try { await api.customers.setStatus(client.id, true); onDelete(); }
    catch (err) { alert(err.message || 'Erreur lors de la réactivation.'); }
    finally { setDeactivating(false); }
  };

  const handlePermanentDelete = async () => {
    if (!window.confirm(
      `Supprimer DÉFINITIVEMENT "${client.name}" ?\n\nCette action est irréversible. ` +
      `Elle n'est possible que si ce client n'a AUCUN historique (facture, commande, etc.) — ` +
      `sinon la loi impose de le conserver 10 ans (Code de commerce, art. L123-22).`
    )) return;
    setDeactivating(true);
    try {
      await api.customers.deletePermanent(client.id);
      onDelete();
    } catch (err) {
      alert(err.message || 'Erreur lors de la suppression définitive.');
    } finally {
      setDeactivating(false);
    }
  };

  const row = (label, value) => value != null && value !== '' ? (
    <div key={label} style={{ background:'#f9fafb', borderRadius:8, padding:'8px 10px' }}>
      <div style={{ fontSize:10, color:'var(--text-muted)', marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:13, fontWeight:600, wordBreak:'break-word' }}>{String(value)}</div>
    </div>
  ) : null;

  const section = (title, fields) => {
    const rows = fields.map(([l,v]) => row(l, v)).filter(Boolean);
    if (!rows.length) return null;
    return (
      <div style={{ marginBottom:16 }}>
        <div style={{ fontWeight:700, fontSize:11, color:'var(--text-muted)', textTransform:'uppercase', marginBottom:8 }}>{title}</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>{rows}</div>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:640, maxHeight:'90vh', overflowY:'auto' }}>
        <div className="modal-header">
          <h3 className="modal-title">🏢 {client.name}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
          {client.status && <span style={{ padding:'3px 10px', borderRadius:12, background:`${STATUS_COLORS[client.status]}18`, color:STATUS_COLORS[client.status], fontSize:11, fontWeight:700 }}>{client.status.replace('_',' ')}</span>}
          {client.legal_form && <span style={{ padding:'3px 10px', borderRadius:12, background:'#6366f118', color:'#6366f1', fontSize:11, fontWeight:700 }}>{client.legal_form}</span>}
          {client.currency && <span style={{ padding:'3px 10px', borderRadius:12, background:'#ba751718', color:'#ba7517', fontSize:11, fontWeight:700 }}>{client.currency}</span>}
          {client.payment_terms && <span style={{ padding:'3px 10px', borderRadius:12, background:'#1a9e7218', color:'#1a9e72', fontSize:11, fontWeight:700 }}>{client.payment_terms}</span>}
        </div>

        {section('Identité', [
          ['Email', client.email], ['Email facturation', client.email_billing],
          ['Téléphone', client.phone], ['Téléphone 2', client.phone2],
          ['Site web', client.website], ['Secteur', client.sector],
        ])}
        {section('Adresse', [
          ['Adresse', [client.address, client.address2].filter(Boolean).join(', ')],
          ['Ville', [client.postal_code, client.city].filter(Boolean).join(' ')],
          ['Pays', client.country],
        ])}
        {section('Identification légale', [
          ['SIREN', client.siren], ['SIRET', client.siret],
          ['N° TVA', client.tva_number], ['Code NAF', client.naf_code],
          ['Forme juridique', client.legal_form], ['Capital social', client.share_capital != null ? `${fmt(client.share_capital)} €` : null],
          ['RCS', client.rcs_number],
        ])}
        {section('Coordonnées bancaires', [
          ['Banque', client.bank_name], ['IBAN', client.bank_iban_masked],
          ['BIC', client.bank_bic],
          ['Banque 2', client.bank2_name], ['IBAN 2', client.bank2_iban_masked],
        ])}
        {section('Conditions commerciales', [
          ['Paiement', client.payment_terms],
          ['Délai', client.payment_delay_days != null ? `${client.payment_delay_days}j` : null],
          ['Plafond', client.credit_limit != null ? `${fmt(client.credit_limit)} €` : null],
          ['Remise', client.discount_rate != null ? `${client.discount_rate}%` : null],
          ['Mode de paiement', client.payment_method],
        ])}
        {section('Contact principal', [
          ['Nom', [client.contact_civility, client.contact_firstname, client.contact_lastname].filter(Boolean).join(' ')],
          ['Rôle', client.contact_role],
          ['Email', client.contact_email], ['Tél.', client.contact_phone],
        ])}

        <CompanyAllocationSection client={client} />
        <SalesRepSection client={client} currentCompanyId={currentCompanyId} />

        {client.notes && (
          <div style={{ padding:'10px 14px', background:'#f9fafb', borderRadius:8, marginBottom:16, fontSize:13, color:'var(--text-muted)', borderLeft:'3px solid var(--border-color,#e2e8f0)' }}>
            {client.notes}
          </div>
        )}

        <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:16 }}>
          Créé le {fmtDate(client.created_at)}{client.updated_at ? ` · Modifié le ${fmtDate(client.updated_at)}` : ''}
        </div>

        <div className="btn-group" style={{ justifyContent:'space-between' }}>
          {client.is_active ? (
            <button className="btn" style={{ color:'#e24b4a', borderColor:'#e24b4a40', fontSize:12 }}
              onClick={handleDeactivate} disabled={deactivating}>
              {deactivating ? 'Désactivation…' : '🚫 Désactiver'}
            </button>
          ) : (
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn" style={{ color:'#1a9e72', borderColor:'#1a9e7240', fontSize:12 }}
                onClick={handleReactivate} disabled={deactivating}>
                {deactivating ? 'Réactivation…' : '✅ Réactiver'}
              </button>
              <button className="btn" style={{ color:'#e24b4a', borderColor:'#e24b4a40', fontSize:12 }}
                onClick={handlePermanentDelete} disabled={deactivating} title="Possible uniquement si aucun historique (facture, commande...)">
                🗑 Supprimer définitivement
              </button>
            </div>
          )}
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn" onClick={onClose}>Fermer</button>
            <button className="btn primary" onClick={onEdit}>✏️ Modifier</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page principale Clients ─────────────────────────────────────
export default function Clients({ currentCompanyId }) {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [sort, setSort]           = useState('name');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'inactive' | 'all'
  const [order, setOrder]         = useState('asc');
  const limit                     = 50;
  const [offset, setOffset]       = useState(0);
  const [mode, setMode]           = useState(null); // null | 'create' | 'edit' | 'detail'
  const [showImportArchive, setShowImportArchive] = useState(false);
  const [selected, setSelected]   = useState(null);
  const [error, setError]         = useState('');

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.customers.list(currentCompanyId, search, { sort, order, limit, offset, status: statusFilter })
      .then(r => {
        const rows = Array.isArray(r) ? r : (r?.data || r?.customers || []);
        const tot  = r?.total ?? rows.length;
        setCustomers(rows);
        setTotal(tot);
      })
      .catch(e => setError(e.message || 'Erreur'))
      .finally(() => setLoading(false));
  }, [currentCompanyId, search, sort, order, limit, offset, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (col) => {
    if (sort === col) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSort(col); setOrder('asc'); }
    setOffset(0);
  };

  const pages = Math.ceil(total / limit);
  const page  = Math.floor(offset / limit);

  const sortArrow = (col) => sort === col ? (order === 'asc' ? ' ↑' : ' ↓') : '';

  if (!currentCompanyId) return (
    <div style={{ textAlign:'center', padding:80, color:'var(--text-muted)' }}>
      <div style={{ fontSize:48, marginBottom:16 }}>🏢</div>
      <div style={{ fontSize:16, fontWeight:600 }}>Sélectionnez une société pour accéder aux clients</div>
    </div>
  );

  return (
    <div>
      <div className="page-header" style={{ marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h2 className="page-title" style={{ marginBottom:0 }}>Clients</h2>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn" onClick={() => setShowImportArchive(true)}>
              📥 Importer archives factures
            </button>
            <button className="btn primary" onClick={() => { setSelected(null); setMode('create'); }}>
              + Nouveau client
            </button>
          </div>
        </div>
      </div>

      {error && <div className="alert danger" style={{ marginBottom:12 }}>{error}</div>}

      {/* Filtres */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <input className="form-control" placeholder="🔍 Nom, email, SIREN, SIRET…"
          style={{ flex:1, minWidth:200 }} value={search}
          onChange={e => { setSearch(e.target.value); setOffset(0); }} />
        <select className="form-control" style={{ width:'auto' }} value={sort}
          onChange={e => { setSort(e.target.value); setOffset(0); }}>
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className="form-control" style={{ width:'auto' }} value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setOffset(0); }}>
          <option value="active">Actifs</option>
          <option value="inactive">Inactifs</option>
          <option value="all">Tous</option>
        </select>
        <button className="btn" onClick={() => setOrder(o => o === 'asc' ? 'desc' : 'asc')}>
          {order === 'asc' ? '↑ Croissant' : '↓ Décroissant'}
        </button>
        <span style={{ fontSize:12, color:'var(--text-muted)' }}>{total} client(s)</span>
      </div>

      {/* Table */}
      <div className="card" style={{ overflowX:'auto' }}>
        {loading ? (
          <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>Chargement…</div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th onClick={() => toggleSort('name')} style={{ cursor:'pointer' }}>Nom{sortArrow('name')}</th>
                <th onClick={() => toggleSort('status')} style={{ cursor:'pointer' }}>Statut{sortArrow('status')}</th>
                <th>SIRET</th>
                <th onClick={() => toggleSort('city')} style={{ cursor:'pointer' }}>Ville{sortArrow('city')}</th>
                <th onClick={() => toggleSort('payment_terms')} style={{ cursor:'pointer' }}>Paiement{sortArrow('payment_terms')}</th>
                <th onClick={() => toggleSort('credit_limit')} style={{ cursor:'pointer', textAlign:'right' }}>Plafond{sortArrow('credit_limit')}</th>
                <th>Banque / IBAN</th>
                <th onClick={() => toggleSort('created_at')} style={{ cursor:'pointer' }}>Créé le{sortArrow('created_at')}</th>
                <th style={{ width:80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map(c => (
                <tr key={c.id} onClick={() => { setSelected(c); setMode('detail'); }} style={{ cursor:'pointer' }}>
                  <td>
                    <div style={{ fontWeight:700 }}>{c.name}</div>
                    {c.trade_name && <div style={{ fontSize:11, color:'var(--text-muted)' }}>{c.trade_name}</div>}
                    {c.email && <div style={{ fontSize:11, color:'var(--text-muted)' }}>{c.email}</div>}
                  </td>
                  <td>
                    {c.status ? (
                      <span style={{ fontSize:11, padding:'2px 8px', borderRadius:8, background:`${STATUS_COLORS[c.status] || '#64748b'}18`, color:STATUS_COLORS[c.status] || '#64748b', fontWeight:700 }}>
                        {c.status.replace('_',' ')}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ fontSize:11, fontFamily:'monospace' }}>{c.siret || c.siren || '—'}</td>
                  <td style={{ fontSize:12 }}>{[c.postal_code, c.city].filter(Boolean).join(' ') || '—'}</td>
                  <td>
                    {c.payment_terms ? (
                      <span style={{ fontSize:11, padding:'2px 8px', borderRadius:8, background:'#1a9e7218', color:'#1a9e72', fontWeight:700 }}>{c.payment_terms}</span>
                    ) : '—'}
                  </td>
                  <td style={{ textAlign:'right', fontSize:12, fontWeight:600, color:'var(--g,#1a9e72)' }}>
                    {c.credit_limit != null ? `${fmt(c.credit_limit)} €` : '—'}
                  </td>
                  <td style={{ fontSize:12 }}>
                    {c.bank_name && <div style={{ fontWeight:600 }}>{c.bank_name}</div>}
                    {c.bank_iban_masked && <div style={{ fontFamily:'monospace', fontSize:10, color:'#185fa5', letterSpacing:1 }}>{c.bank_iban_masked}</div>}
                    {!c.bank_name && !c.bank_iban_masked && '—'}
                  </td>
                  <td style={{ fontSize:11, color:'var(--text-muted)' }}>{fmtDate(c.created_at)}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display:'flex', gap:4 }}>
                      <button className="btn sm" onClick={() => { setSelected(c); setMode('detail'); }} title="Voir">👁</button>
                      <button className="btn sm" onClick={() => { setSelected(c); setMode('edit'); }} title="Modifier">✏️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {customers.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign:'center', padding:32, color:'var(--text-muted)' }}>
                  Aucun client{search ? ` pour "${search}"` : ''}
                  {!search && <div style={{ marginTop:8 }}>
                    <button className="btn primary" onClick={() => setMode('create')}>+ Créer le premier client</button>
                  </div>}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display:'flex', gap:6, justifyContent:'center', marginTop:16, alignItems:'center' }}>
          <button className="btn sm" disabled={page === 0} onClick={() => setOffset(0)}>«</button>
          <button className="btn sm" disabled={page === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>‹</button>
          <span style={{ fontSize:12, color:'var(--text-muted)' }}>Page {page+1}/{pages}</span>
          <button className="btn sm" disabled={page >= pages-1} onClick={() => setOffset(offset + limit)}>›</button>
          <button className="btn sm" disabled={page >= pages-1} onClick={() => setOffset((pages-1)*limit)}>»</button>
        </div>
      )}

      {/* Modal création */}
      {mode === 'create' && (
        <FormModal title="➕ Nouveau client">
          <CustomerForm
            onSaved={() => { setMode(null); load(); }}
            onCancel={() => setMode(null)}
          />
        </FormModal>
      )}

      {/* Modal édition */}
      {mode === 'edit' && selected && (
        <FormModal title={`✏️ Modifier — ${selected.name}`}>
          <CustomerForm
            customerId={selected.id}
            onSaved={() => { setMode(null); setSelected(null); load(); }}
            onCancel={() => setMode(null)}
          />
        </FormModal>
      )}

      {/* Modal détail */}
      {mode === 'detail' && selected && (
        <DetailModal
          client={selected}
          currentCompanyId={currentCompanyId}
          onEdit={() => setMode('edit')}
          onDelete={() => { setMode(null); setSelected(null); load(); }}
          onClose={() => { setMode(null); setSelected(null); }}
        />
      )}

      {showImportArchive && (
        <ImportInvoiceArchive
          currentCompanyId={currentCompanyId}
          onClose={() => { setShowImportArchive(false); load(); }}
        />
      )}
    </div>
  );
}
